import React, { useEffect, useState } from 'react';
import { Box, Button, FormHelperText, LinearProgress, OutlinedInput, Typography } from '@mui/material';
import { RowAction } from '../../../components/row-action';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ImageIcon from '@mui/icons-material/Image';
import ClearIcon from '@mui/icons-material/Clear';
import ImageAppService from '../../../application/platform/apm/image/image-app-service';
import { IMAGE_ACCEPT, IMAGE_MAX_SIZE_MB, buildImageUrl, imageDisplayName, validateImageFile } from './image-utils';

interface ImageUploadFieldProps {
    id: string;
    value?: string;
    onChange: (fileName?: string) => void;
    onUploadingChange?: (uploading: boolean) => void;
    error?: boolean;
    helperText?: string;
    previewBaseUrl?: string;
    clearable?: boolean;
}

export function ImageUploadField({ id, value, onChange, onUploadingChange, error, helperText, previewBaseUrl, clearable = true }: ImageUploadFieldProps) {
    const [originalName, setOriginalName] = useState<string>();
    // URL devolvida pelo upload: pré-visualiza na hora, sem depender da URL base
    const [uploaded, setUploaded] = useState<{ fileName?: string; url?: string }>();
    const [previewFailed, setPreviewFailed] = useState(false);
    const [uploadError, setUploadError] = useState<string>();
    const [progress, setProgress] = useState<number>();
    const [dragActive, setDragActive] = useState(false);
    const uploading = progress !== undefined;
    const previewUrl = uploaded?.url && uploaded.fileName === value ? uploaded.url : buildImageUrl(value, previewBaseUrl);

    useEffect(() => {
        if (!value) setOriginalName(undefined);
    }, [value]);

    useEffect(() => {
        setPreviewFailed(false);
    }, [previewUrl]);

    async function handleFile(file?: File) {
        if (!file || uploading) return;
        setUploadError(undefined);

        const validationError = validateImageFile(file);
        if (validationError) {
            setUploadError(validationError);
            return;
        }

        setProgress(0);
        onUploadingChange?.(true);
        try {
            const result = await ImageAppService.upload(file, setProgress);
            setOriginalName(result.originalFileName || file.name);
            setUploaded({ fileName: result.fileName, url: result.url });
            onChange(result.fileName);
        } catch (e: any) {
            setUploadError(e?.message || 'Não foi possível enviar a imagem.');
        } finally {
            setProgress(undefined);
            onUploadingChange?.(false);
        }
    }

    function handleDrag(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    }

    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        handleFile(e.dataTransfer.files?.[0]);
    }

    const displayName = originalName || imageDisplayName(value);
    const hasError = error || !!uploadError;

    return (
        <Box onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}>
            <input
                style={{ display: 'none' }}
                id={id}
                type="file"
                accept={IMAGE_ACCEPT}
                onChange={(e) => {
                    handleFile(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Box
                    sx={{
                        width: 56, height: 56, flexShrink: 0, borderRadius: 1, border: '1px dashed', overflow: 'hidden',
                        borderColor: dragActive ? 'primary.main' : 'divider',
                        bgcolor: dragActive ? 'action.hover' : 'background.default',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    {previewUrl && !previewFailed
                        ? <Box component="img" src={previewUrl} alt={displayName} onError={() => setPreviewFailed(true)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <ImageIcon color={value ? 'primary' : 'disabled'} />}
                </Box>
                <OutlinedInput
                    fullWidth
                    readOnly
                    value={displayName}
                    placeholder={dragActive ? 'Solte a imagem aqui' : 'Selecione ou arraste uma imagem'}
                    error={hasError}
                    endAdornment={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {clearable && value && !uploading && (
                                <RowAction title="Remover imagem" onClick={() => onChange(undefined)}><ClearIcon fontSize="small" /></RowAction>
                            )}
                            <label htmlFor={id}>
                                <Button variant="contained" component="span" disabled={uploading} sx={{ px: 2 }} endIcon={<AddPhotoAlternateIcon />}>
                                    ...
                                </Button>
                            </label>
                        </Box>
                    }
                />
            </Box>
            {uploading && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />}
            {uploadError
                ? <FormHelperText error>{uploadError}</FormHelperText>
                : helperText
                    ? <FormHelperText error={error}>{helperText}</FormHelperText>
                    : <Typography variant="caption" color="text.secondary">JPG, PNG, WEBP ou GIF até {IMAGE_MAX_SIZE_MB} MB</Typography>}
        </Box>
    );
}
